import { useRef } from "react";
import { useCatalogLayoutTransition } from "@/features/catalog/hooks/useCatalogLayoutTransition";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { resolveCatalogFontScaleValue } from "@/features/catalog/utils/catalogAppearance";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { useComponentNode } from "@/shared/ui/component-hierarchy/hooks";

interface DesktopCatalogGridLayoutProps {
  columns: number;
  imageSize: number;
  children: React.ReactNode;
}

/** PC版カタログ形式の一覧で共有する、全幅スクロール可能なグリッド外枠。 */
export function DesktopCatalogGridLayout({
  columns,
  imageSize,
  children,
}: DesktopCatalogGridLayoutProps): React.ReactElement {
  // 文字倍率はカタログ・過去ログ・履歴で共通の外観設定なので、
  // 呼び出し元ごとにpropを通さずここで直接読む。
  const fontScale = useCatalogStore((state) => state.catalogFontScale);
  const node = useComponentNode();

  return (
    <ComponentScope node={node}>
      <DesktopCatalogGridLayoutView
        columns={columns}
        imageSize={imageSize}
        fontScale={fontScale}
      >
        {children}
      </DesktopCatalogGridLayoutView>
    </ComponentScope>
  );
}

type DesktopCatalogGridLayoutViewProps = DesktopCatalogGridLayoutProps & {
  readonly fontScale: number;
};

export function DesktopCatalogGridLayoutView({
  columns,
  imageSize,
  children,
  fontScale,
}: DesktopCatalogGridLayoutViewProps): React.ReactElement {
  const gridRef = useRef<HTMLDivElement>(null);
  useCatalogLayoutTransition(gridRef);

  return (
    <section
      className="desktop-catalog-grid-wrap"
      aria-label="スレッドカタログ"
    >
      <div
        ref={gridRef}
        className="desktop-catalog-grid"
        style={
          {
            "--catalog-columns": columns,
            "--catalog-image-size": `${imageSize}px`,
            "--catalog-font-scale": resolveCatalogFontScaleValue(fontScale),
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </section>
  );
}
